import React, { useState, useEffect } from "react";
import { cn } from "../../lib/utils";
import sanityClient from "../../client";

export function Projects({ className, ...props }) {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "projects"] {
          Project_Name,
          Project_Link,
          Project_Link_Icon,
          Description,
          Sub_Description,
          Icons_For_Tech_Used
        }`
      )
      .then((data) => {
        setProjects(data);
        setIsLoading(false);
        if (data.length === 0) {
          setError(true);
        }
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
        setError(true);
      });
  }, []);

  useEffect(() => {
    if (!showModal) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setCurrentProject(null);
        setShowModal(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showModal]);

  const openProject = (project) => {
    setCurrentProject(project);
    setShowModal(true);
  };

  const closeModal = () => {
    setCurrentProject(null);
    setShowModal(false);
  };

  return (
    <section
      id="projects"
      className={cn(
        "mx-auto w-full max-w-[40rem] px-6 pt-28 pb-16 sm:pt-36 sm:pb-24",
        className
      )}
      {...props}
    >
      <h1 className="text-3xl sm:text-[2.5rem] font-medium tracking-[-0.03em] text-darkgray">
        Projects
      </h1>

      {isLoading ? (
        <ul className="mt-12 border-t border-border" aria-hidden="true">
          {[...Array(4)].map((_, i) => (
            <li key={i} className="py-6 border-b border-border space-y-2.5">
              <div className="loading-skeleton h-4 w-1/3 rounded-sm"></div>
              <div className="loading-skeleton h-3 w-full rounded-sm"></div>
            </li>
          ))}
        </ul>
      ) : error ? (
        <p className="mt-10 text-[15px] text-gray">
          Projects are currently unavailable.
        </p>
      ) : (
        <ul className="mt-12 border-t border-border">
          {projects.map((project, index) => (
            <li key={index} className="border-b border-border">
              <button
                type="button"
                onClick={() => openProject(project)}
                className="group w-full py-6 text-left focus-ring rounded-sm"
                aria-label={`View details for ${project.Project_Name}`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="text-[16px] sm:text-[17px] font-medium text-darkgray">
                    {project.Project_Name}
                  </h2>
                  <span className="text-lightgray transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-darkgray">
                    →
                  </span>
                </div>

                {project.Sub_Description && (
                  <p className="mt-2 text-[15px] leading-relaxed text-gray">
                    {project.Sub_Description}
                  </p>
                )}

                {project.Icons_For_Tech_Used &&
                  project.Icons_For_Tech_Used.length > 0 && (
                    <div className="mt-3 flex items-center gap-2.5 text-lightgray">
                      {project.Icons_For_Tech_Used.map((tech, i) => (
                        <div
                          key={i}
                          className="text-[15px]"
                          dangerouslySetInnerHTML={{ __html: tech }}
                          title="Technology used"
                        />
                      ))}
                    </div>
                  )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showModal && currentProject && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-modal-title"
        >
          <div
            className="absolute inset-0 bg-darkgray/30"
            onClick={closeModal}
          ></div>
          <div className="relative z-[101] w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white border border-border p-6 sm:p-8">
            <div className="flex items-start justify-between gap-6">
              <h2
                id="project-modal-title"
                className="text-xl font-medium tracking-tight text-darkgray"
              >
                {currentProject.Project_Name}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-[13px] text-lightgray hover:text-darkgray focus-ring rounded-sm"
                aria-label="Close"
              >
                Close
              </button>
            </div>

            <p className="mt-5 text-[15px] leading-relaxed text-gray">
              {currentProject.Description}
            </p>

            {currentProject.Icons_For_Tech_Used &&
              currentProject.Icons_For_Tech_Used.length > 0 && (
                <div className="mt-6 flex items-center gap-3 text-gray">
                  {currentProject.Icons_For_Tech_Used.map((tech, i) => (
                    <div
                      key={i}
                      className="text-lg"
                      dangerouslySetInnerHTML={{ __html: tech }}
                      title="Technology used"
                    />
                  ))}
                </div>
              )}

            {currentProject.Project_Link && (
              <a
                href={currentProject.Project_Link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 text-[15px] text-darkgray underline underline-offset-4 decoration-border hover:decoration-darkgray focus-ring rounded-sm"
              >
                Visit project
                <span aria-hidden="true">→</span>
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
